import { FC, useEffect, useMemo, useState } from 'react';

import { GettingFileResolutionDto, InternalDashboardTableStatsDto, InvoiceFileProcessingDto } from 'src/generated/services/TFinancialApi';
import { useGetIssues, useGetFiles, useGetActionNeeded } from 'src/use-cases/internalDashboard/useFileResolution';
import FilterNav from '../../molecules/dataTable/FilterNav';
import { IFilterOptionDTO, IInternalDashboardContentProps, SORTING_DIRECTION } from './types';
import { InternalTableIssues } from './TableIssues';
import { InternalTableFiles } from './TableFiles';
import { useInternalDashboardSelector } from './Selector';

const defaultFilterOptions: IFilterOptionDTO[] = [
  {
    value: 'ACTION_NEEDED',
    label: 'Action Needed',
    count: 0,
  },
  {
    value: 'ALL_ISSUES',
    label: 'All Issues',
    count: 0,
  },
  {
    value: 'ALL_FILES',
    label: 'All Files',
    count: 0,
  },
];

export const InternalDashboardContent: FC<IInternalDashboardContentProps> = () => {
  const [totalActionNeeded, setTotalActionNeeded] = useState<number>(0);
  const [actionNeeded, setActionNeeded] = useState<GettingFileResolutionDto[]>([]);
  const [totalIssues, setTotalIssues] = useState<number>(0);
  const [issues, setIssues] = useState<GettingFileResolutionDto[]>([]);
  const [totalFiles, setTotalFiles] = useState<number>(0);
  const [files, setFiles] = useState<InvoiceFileProcessingDto[]>([]);
  const [selectedNav, setSelectedNav] = useState<string>('ACTION_NEEDED');
  const [filterOptions, setFilterOptions] = useState<IFilterOptionDTO[]>(defaultFilterOptions);

  const { filterParams, setFilterPage, setFilterTabName, setFilterStatuses, setSorting } = useInternalDashboardSelector();

  const {
    data: dataActionNeeded,
    isLoading: isLoadingActionNeeded,
    refetch: refetchActionNeeded,
  } = useGetActionNeeded(filterParams, selectedNav === 'ACTION_NEEDED');

  const { data: dataIssues, isLoading: isLoadingIssues, refetch: refetchIssues } = useGetIssues(filterParams, selectedNav === 'ALL_ISSUES');

  const { data: dataFiles, isLoading: isLoadingFiles, refetch: refetchFiles } = useGetFiles(filterParams, selectedNav === 'ALL_FILES');

  const parseTableStats = (data: InternalDashboardTableStatsDto) => {
    const newFilterOptions = defaultFilterOptions.map((item) => {
      let itemCount = 0;
      switch (item.value) {
        case 'ACTION_NEEDED':
          itemCount = data?.actionNeeded;
          break;
        case 'ALL_ISSUES':
          itemCount = data?.allIssues;
          break;
        case 'ALL_FILES':
          itemCount = data?.allFiles;
          break;
        default:
          itemCount = 0;
      }
      return {
        ...item,
        count: itemCount,
      };
    });

    setFilterOptions(newFilterOptions);
  };

  useEffect(() => {
    if (dataActionNeeded && dataActionNeeded?.tableData?.content?.length > 0) {
      setActionNeeded(dataActionNeeded.tableData.content);
      setTotalActionNeeded(dataActionNeeded?.tableData?.totalElements || 0);
    } else {
      setActionNeeded([]);
      setTotalActionNeeded(0);
    }
    if (dataActionNeeded?.tableStats) {
      parseTableStats(dataActionNeeded.tableStats);
    }
  }, [dataActionNeeded]);

  useEffect(() => {
    if (dataIssues && dataIssues?.tableData?.content?.length > 0) {
      setIssues(dataIssues.tableData.content);
      setTotalIssues(dataIssues?.tableData?.totalElements || 0);
    } else {
      setIssues([]);
      setTotalIssues(0);
    }
    if (dataIssues?.tableStats) {
      parseTableStats(dataIssues.tableStats);
    }
  }, [dataIssues]);

  useEffect(() => {
    if (dataFiles && dataFiles?.tableData?.content?.length > 0) {
      setFiles(dataFiles.tableData.content);
      setTotalFiles(dataFiles?.tableData?.totalElements || 0);
    } else {
      setFiles([]);
      setTotalFiles(0);
    }
    if (dataFiles?.tableStats) {
      parseTableStats(dataFiles.tableStats);
    }
  }, [dataFiles]);

  useEffect(() => {
    setFilterPage(1);
  }, [filterParams.fileResolutionStatuses, setFilterPage]);

  const onChangeFilterNav = (value: string) => {
    setFilterStatuses([]);
    setSelectedNav(value);
    setFilterTabName(value);
  };

  const onSorting = (method: SORTING_DIRECTION, fieldName: string) => {
    setSorting(fieldName, method);
  };

  const totalItems = useMemo(() => {
    if (selectedNav === 'ALL_FILES') {
      return totalFiles;
    }
    if (selectedNav === 'ALL_ISSUES') {
      return totalIssues;
    }

    return totalActionNeeded;
  }, [selectedNav, totalFiles, totalIssues, totalActionNeeded]);

  return (
    <div className='w-full pt-24  pb-40'>
      <div className=' border-t-1 border-neutral-20 '>
        <FilterNav
          options={filterOptions}
          onChange={onChangeFilterNav}
          defaultSelected='ACTION_NEEDED'
          onRefresh={() => {
            if (selectedNav === 'ACTION_NEEDED') {
              refetchActionNeeded();
            } else if (selectedNav === 'ALL_ISSUES') {
              refetchIssues();
            } else if (selectedNav === 'ALL_FILES') {
              refetchFiles();
            }
          }}
          isLoadingItems={isLoadingIssues || isLoadingFiles || isLoadingActionNeeded}
          isLoadingExport={false}
          isShowPagination
          params={{
            page: filterParams?.page || 1,
          }}
          numberOfRows={filterParams?.size || 25}
          total={totalItems}
          onChangePage={(value) => {
            setFilterPage(value);
          }}
        />
        <div className='w-full bg-blue_grey-5 overflow-hidden '>
          {selectedNav === 'ALL_FILES' && <InternalTableFiles data={files} totalItems={totalFiles} onSorting={onSorting} />}

          {selectedNav === 'ALL_ISSUES' && <InternalTableIssues key='ALL_ISSUES' data={issues} totalItems={totalIssues} onSorting={onSorting} />}

          {selectedNav === 'ACTION_NEEDED' && (
            <InternalTableIssues key='ACTION_NEEDED' data={actionNeeded} totalItems={totalActionNeeded} onSorting={onSorting} />
          )}
        </div>
      </div>
    </div>
  );
};
